/**
 * ExpensesTab.js — Admin paneli "İşletme Finans" sekmesi
 * v3: Tema uyumlu — tüm renkler CSS değişkenlerinden alınır
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  IcBolt, IcDroplet, IcFire, IcHouse, IcBox, IcMemo, IcMoney,
  IcTrend, IcClipboard, IcSave, IcWarning, IcTrash,
} from "./Icons";

const API = process.env.REACT_APP_API_URL ?? "";

const TR_MONTHS = [
  "", "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];

async function apiFetch(url, opts = {}) {
  const res = await fetch(url, {
    ...opts,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

// ── Z Raporu Bölümü ───────────────────────────────────────────────────────────
// Akşam çekilen Z fişi fotoğrafı → Claude Vision okur → kullanıcı kontrol eder
// → kaydet → tek tuşla muhasebeciye mail. OCR sonucu asla otomatik kaydedilmez.
function ZReportSection({ year, month }) {
  const monthStr = `${year}-${String(month).padStart(2, "0")}`;
  const [reports, setReports]   = useState([]);
  const [accountant, setAccountant] = useState("");
  const [accEdit, setAccEdit]   = useState(false);
  const [accDraft, setAccDraft] = useState("");
  const [ocrBusy, setOcrBusy]   = useState(false);
  const [draft, setDraft]       = useState(null); // OCR sonrası düzenlenebilir taslak
  const [msg, setMsg]           = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const fileRef = React.useRef(null);

  const flash = (ok, text) => { setMsg({ ok, text }); setTimeout(() => setMsg(null), 5000); };

  const load = useCallback(async () => {
    try {
      const [r, a] = await Promise.all([
        apiFetch(`${API}/api/admin/z-reports?month=${monthStr}`),
        apiFetch(`${API}/api/admin/z-reports/settings/accountant`),
      ]);
      setReports(r.reports || []);
      setAccountant(a.email || "");
    } catch { /* liste yüklenemezse sessiz — kart yine çalışır */ }
  }, [monthStr]);

  useEffect(() => { load(); }, [load]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setOcrBusy(true); setDraft(null); setMsg(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/api/admin/z-reports/ocr`, {
        method: "POST", credentials: "include", body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const d = await res.json();
      const x = d.extracted || {};
      if (x.is_z_report === false) {
        flash(false, "Bu fotoğraf Z raporu gibi görünmüyor: " + (x.warnings?.[0] || "içerik tanınamadı"));
        return;
      }
      setDraft({
        report_date:   x.report_date || new Date().toISOString().slice(0, 10),
        total:         x.total ?? "",
        cash:          x.cash ?? "",
        card:          x.card ?? "",
        vat:           x.vat ?? "",
        receipt_count: x.receipt_count ?? "",
        z_no:          x.z_no || "",
        image_path:    d.image_path,
        raw_ocr_json:  d.raw,
        confidence:    x.confidence,
        warnings:      x.warnings || [],
      });
    } catch (err) {
      flash(false, err.message);
    } finally {
      setOcrBusy(false);
    }
  };

  const saveDraft = async () => {
    if (!draft) return;
    if (draft.total === "" || isNaN(Number(draft.total))) { flash(false, "Toplam tutar zorunlu"); return; }
    try {
      await apiFetch(`${API}/api/admin/z-reports`, {
        method: "POST",
        body: JSON.stringify({
          report_date:   draft.report_date,
          total:         Number(draft.total) || 0,
          cash:          Number(draft.cash) || 0,
          card:          Number(draft.card) || 0,
          vat:           Number(draft.vat) || 0,
          receipt_count: parseInt(draft.receipt_count, 10) || 0,
          z_no:          draft.z_no || null,
          image_path:    draft.image_path,
          raw_ocr_json:  draft.raw_ocr_json,
        }),
      });
      setDraft(null);
      flash(true, "✓ Z raporu kaydedildi");
      load();
    } catch (err) { flash(false, err.message); }
  };

  const sendReport = async (id) => {
    if (!accountant) { flash(false, "Önce muhasebeci e-postasını girin (sağ üstteki ✎)"); return; }
    setSendingId(id);
    try {
      const d = await apiFetch(`${API}/api/admin/z-reports/${id}/send`, { method: "POST" });
      flash(true, `✓ Muhasebeciye gönderildi (${d.sent_to})`);
      load();
    } catch (err) { flash(false, err.message); }
    finally { setSendingId(null); }
  };

  const deleteReport = async (id) => {
    if (!window.confirm("Bu Z raporu kaydı silinsin mi?")) return;
    try { await apiFetch(`${API}/api/admin/z-reports/${id}`, { method: "DELETE" }); load(); }
    catch (err) { flash(false, err.message); }
  };

  const saveAccountant = async () => {
    try {
      await apiFetch(`${API}/api/admin/z-reports/settings/accountant?email=${encodeURIComponent(accDraft.trim())}`, { method: "PUT" });
      setAccountant(accDraft.trim());
      setAccEdit(false);
      flash(true, "✓ Muhasebeci e-postası kaydedildi");
    } catch (err) { flash(false, err.message); }
  };

  const inp = {
    background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 7,
    color: "var(--text)", padding: "8px 10px", fontSize: 13, outline: "none", width: "100%",
    boxSizing: "border-box",
  };
  const lbl = { fontSize: 11, color: "var(--text3)", marginBottom: 3, display: "block" };

  const CONF = { high: ["#4CAF50", "Yüksek güven"], medium: ["#f5a623", "Orta güven — kontrol edin"], low: ["#e57373", "Düşük güven — dikkatle kontrol edin"] };

  return (
    <div style={{ background: "var(--surface2)", borderRadius: 12, padding: "18px 20px" }}>
      {/* Başlık + muhasebeci ayarı */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
        <div style={{ fontWeight: 700, color: "var(--text)", fontSize: 14, display: "inline-flex", alignItems: "center", gap: 8 }}>
          <IcMemo size={16} /> Z Raporu
          <span style={{ fontSize: 11, fontWeight: 500, color: "var(--text3)" }}>{TR_MONTHS[month]} {year}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--text3)" }}>
          {accEdit ? (
            <>
              <input style={{ ...inp, width: 220 }} placeholder="muhasebeci@ornek.com"
                     value={accDraft} onChange={e => setAccDraft(e.target.value)}
                     onKeyDown={e => e.key === "Enter" && saveAccountant()} autoFocus />
              <button onClick={saveAccountant} style={{ background: "var(--mint)", color: "#fff", border: "none", borderRadius: 6, padding: "7px 12px", fontSize: 12, cursor: "pointer" }}>Kaydet</button>
              <button onClick={() => setAccEdit(false)} style={{ background: "transparent", color: "var(--text3)", border: "1px solid var(--border)", borderRadius: 6, padding: "7px 10px", fontSize: 12, cursor: "pointer" }}>İptal</button>
            </>
          ) : (
            <>
              <span>Muhasebeci: <b style={{ color: accountant ? "var(--text2)" : "#e57373" }}>{accountant || "tanımlı değil"}</b></span>
              <button onClick={() => { setAccDraft(accountant); setAccEdit(true); }}
                      style={{ background: "transparent", border: "1px solid var(--border)", borderRadius: 6, color: "var(--text2)", padding: "5px 9px", cursor: "pointer", fontSize: 12 }}>✎</button>
            </>
          )}
        </div>
      </div>

      {/* Yükleme */}
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
             capture="environment" style={{ display: "none" }} onChange={handleFile} />
      {!draft && (
        <button
          onClick={() => fileRef.current?.click()}
          disabled={ocrBusy}
          style={{
            width: "100%", padding: "14px 0", borderRadius: 10, fontSize: 14, fontWeight: 700,
            background: "rgba(var(--mint-rgb),0.1)", border: "1.5px dashed rgba(var(--mint-rgb),0.5)",
            color: "var(--mint)", cursor: ocrBusy ? "wait" : "pointer", marginBottom: 14,
          }}
        >
          {ocrBusy ? "Fotoğraf okunuyor… (10-20 sn)" : "Z raporu fotoğrafı yükle — AI okusun"}
        </button>
      )}

      {/* OCR taslağı — kontrol + kaydet */}
      {draft && (
        <div style={{
          background: "var(--surface)", border: "1px solid rgba(var(--mint-rgb),0.4)",
          borderRadius: 10, padding: 14, marginBottom: 14,
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <b style={{ fontSize: 13, color: "var(--text)" }}>Okunan değerler — kontrol edin</b>
            {draft.confidence && (
              <span style={{ fontSize: 11, fontWeight: 700, color: CONF[draft.confidence]?.[0] }}>
                ● {CONF[draft.confidence]?.[1]}
              </span>
            )}
          </div>
          {draft.warnings?.length > 0 && (
            <div style={{
              background: "rgba(245,166,35,0.08)", border: "1px solid rgba(245,166,35,0.35)",
              borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#f5a623", marginBottom: 10,
            }}>
              {draft.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10, marginBottom: 12 }}>
            <div><label style={lbl}>Tarih</label>
              <input style={inp} type="date" value={draft.report_date} onChange={e => setDraft({ ...draft, report_date: e.target.value })} /></div>
            <div><label style={lbl}>Toplam (₺)</label>
              <input style={inp} type="number" step="0.01" value={draft.total} onChange={e => setDraft({ ...draft, total: e.target.value })} /></div>
            <div><label style={lbl}>Nakit (₺)</label>
              <input style={inp} type="number" step="0.01" value={draft.cash} onChange={e => setDraft({ ...draft, cash: e.target.value })} /></div>
            <div><label style={lbl}>Kart (₺)</label>
              <input style={inp} type="number" step="0.01" value={draft.card} onChange={e => setDraft({ ...draft, card: e.target.value })} /></div>
            <div><label style={lbl}>KDV (₺)</label>
              <input style={inp} type="number" step="0.01" value={draft.vat} onChange={e => setDraft({ ...draft, vat: e.target.value })} /></div>
            <div><label style={lbl}>Fiş adedi</label>
              <input style={inp} type="number" value={draft.receipt_count} onChange={e => setDraft({ ...draft, receipt_count: e.target.value })} /></div>
            <div><label style={lbl}>Z No</label>
              <input style={inp} value={draft.z_no} onChange={e => setDraft({ ...draft, z_no: e.target.value })} /></div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={saveDraft} style={{
              flex: 1, background: "rgba(var(--mint-rgb),0.12)", border: "1px solid rgba(var(--mint-rgb),0.35)",
              borderRadius: 8, color: "var(--mint)", padding: "10px 0", fontWeight: 700, fontSize: 13, cursor: "pointer",
            }}>Doğru — Kaydet</button>
            <button onClick={() => setDraft(null)} style={{
              background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 8,
              color: "var(--text2)", padding: "10px 16px", fontSize: 13, cursor: "pointer",
            }}>Vazgeç</button>
          </div>
        </div>
      )}

      {msg && (
        <div style={{ fontSize: 12.5, fontWeight: 600, color: msg.ok ? "#4CAF50" : "#e57373", marginBottom: 10 }}>
          {msg.text}
        </div>
      )}

      {/* Ay listesi */}
      {reports.length === 0 ? (
        <div style={{ color: "var(--text3)", fontSize: 12.5, textAlign: "center", padding: "10px 0" }}>
          Bu ay henüz Z raporu kaydı yok
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {reports.map(r => (
            <div key={r.id} style={{
              display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
              background: "var(--surface)", borderRadius: 8, padding: "9px 12px", fontSize: 12.5,
            }}>
              <b style={{ color: "var(--text)", fontFamily: "monospace" }}>
                {new Date(r.report_date + "T00:00:00").toLocaleDateString("tr-TR")}
              </b>
              <span style={{ color: "var(--text2)" }}>Toplam <b>₺{Number(r.total).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</b></span>
              <span style={{ color: "var(--text3)" }}>Nakit ₺{Number(r.cash).toLocaleString("tr-TR")} · Kart ₺{Number(r.card).toLocaleString("tr-TR")}</span>
              {r.status === "sent" ? (
                <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 700, color: "#4CAF50" }}>✓ Gönderildi</span>
              ) : (
                <button onClick={() => sendReport(r.id)} disabled={sendingId === r.id} style={{
                  marginLeft: "auto", background: "rgba(var(--mint-rgb),0.12)",
                  border: "1px solid rgba(var(--mint-rgb),0.35)", borderRadius: 6,
                  color: "var(--mint)", padding: "6px 12px", fontSize: 11.5, fontWeight: 700,
                  cursor: sendingId === r.id ? "wait" : "pointer",
                }}>{sendingId === r.id ? "Gönderiliyor…" : "Muhasebeciye Gönder"}</button>
              )}
              <button onClick={() => deleteReport(r.id)} title="Sil" style={{
                background: "transparent", border: "1px solid var(--border)", borderRadius: 6,
                color: "var(--text3)", padding: "6px 9px", fontSize: 11.5, cursor: "pointer",
              }}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── SVG Line Chart — 3 çizgi ─────────────────────────────────────────────────
function LineChart({ months }) {
  if (!months || months.length === 0) return (
    <div style={{ color: "var(--text3)", fontSize: 13, padding: "40px 0", textAlign: "center" }}>
      Henüz veri yok
    </div>
  );

  const W = 900, H = 220, PAD = { top: 28, right: 24, bottom: 38, left: 78 };
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;

  const revenues = months.map(m => m.revenue);
  const expenses = months.map(m => m.operating_expenses);
  const profits  = months.map(m => m.true_net_profit);

  const allVals = [...revenues, ...expenses, ...profits, 0];
  const minVal  = Math.min(...allVals);
  const maxVal  = Math.max(...allVals);
  const range   = maxVal - minVal || 1;

  const xOf = i => PAD.left + (i / (months.length - 1 || 1)) * plotW;
  const yOf = v => PAD.top + plotH - ((v - minVal) / range) * plotH;
  const toPath = vals =>
    vals.map((v, i) => `${i === 0 ? "M" : "L"}${xOf(i).toFixed(1)},${yOf(v).toFixed(1)}`).join(" ");

  const zeroY = yOf(0);

  const yLabels = [0, 1, 2, 3].map(i => {
    const val = minVal + (range / 3) * i;
    return { y: yOf(val), label: `₺${Math.round(val).toLocaleString("tr-TR")}` };
  });

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }}>
      <rect x={0} y={0} width={W} height={H} style={{ fill: "var(--surface3)" }} rx={8} />

      {/* Gridlines */}
      {yLabels.map((l, i) => (
        <g key={i}>
          <line x1={PAD.left} y1={l.y} x2={W - PAD.right} y2={l.y}
            style={{ stroke: "var(--border)" }} strokeWidth={1} />
          <text x={PAD.left - 8} y={l.y + 4} textAnchor="end"
            style={{ fill: "var(--text3)" }} fontSize={10}>{l.label}</text>
        </g>
      ))}

      {/* Sıfır çizgisi */}
      {zeroY >= PAD.top && zeroY <= PAD.top + plotH && (
        <line x1={PAD.left} y1={zeroY} x2={W - PAD.right} y2={zeroY}
          style={{ stroke: "var(--border)" }} strokeWidth={1} strokeDasharray="4,3" />
      )}

      {/* Satış Geliri */}
      <path d={toPath(revenues)} fill="none" stroke="#4a7cbf" strokeWidth={1.5} strokeOpacity={0.7} />
      {/* İşletme Giderleri */}
      <path d={toPath(expenses)} fill="none" stroke="#e8a030" strokeWidth={1.5} strokeOpacity={0.85} />
      {/* Net Kâr */}
      <path d={toPath(profits)} fill="none" stroke="#4CAF50" strokeWidth={2.5} />

      {/* Net Kâr noktaları + X etiketleri */}
      {profits.map((v, i) => (
        <g key={i}>
          <circle cx={xOf(i)} cy={yOf(v)} r={3.5}
            fill={v >= 0 ? "#4CAF50" : "#e57373"}
            style={{ stroke: "var(--surface3)" }} strokeWidth={1.5} />
          <text x={xOf(i)} y={H - 6} textAnchor="middle"
            style={{ fill: "var(--text2)" }} fontSize={9}>
            {TR_MONTHS[months[i].month]?.slice(0, 3)}
          </text>
        </g>
      ))}

      {/* Legend */}
      <line x1={PAD.left} y1={16} x2={PAD.left + 16} y2={16} stroke="#4a7cbf" strokeWidth={1.5} strokeOpacity={0.7} />
      <text x={PAD.left + 20} y={20} style={{ fill: "var(--text2)" }} fontSize={10}>Satış Geliri</text>

      <line x1={PAD.left + 92} y1={16} x2={PAD.left + 108} y2={16} stroke="#e8a030" strokeWidth={1.5} />
      <text x={PAD.left + 112} y={20} style={{ fill: "var(--text2)" }} fontSize={10}>İşletme Giderleri</text>

      <line x1={PAD.left + 232} y1={16} x2={PAD.left + 248} y2={16} stroke="#4CAF50" strokeWidth={2.5} />
      <text x={PAD.left + 252} y={20} style={{ fill: "var(--text2)" }} fontSize={10}>Gerçek Net Kâr</text>
    </svg>
  );
}

// ── Yardımcı ─────────────────────────────────────────────────────────────────
function fmt(n) {
  return `₺${(n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ── Silme Modalı ─────────────────────────────────────────────────────────────
const _overlay  = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 };

function ConfirmPinModal({ title, warning, onConfirm, onClose }) {
  const [error, setError] = useState("");
  const [busy,  setBusy]  = useState(false);

  const handle = async () => {
    setBusy(true); setError("");
    try { await onConfirm(); }
    catch (e) { setError(e.message || "Bir hata oluştu"); setBusy(false); }
  };

  return (
    <div style={_overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: "var(--surface)", border: "1px solid var(--border)",
        borderRadius: 12, padding: "24px 28px", maxWidth: 380, width: "90%",
      }}>
        <div style={{ fontWeight: 700, color: "#e57373", fontSize: 16, marginBottom: 12, display:"inline-flex", alignItems:"center", gap:8 }}><IcWarning size={17} /> {title}</div>
        <div style={{ color: "var(--text2)", fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>{warning}</div>
        {error && <div style={{ color: "#e57373", fontSize: 13, marginBottom: 10 }}>{error}</div>}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} disabled={busy} style={{
            background: "var(--surface2)", border: "1px solid var(--border)",
            borderRadius: 8, color: "var(--text2)", padding: "9px 18px", cursor: "pointer", fontSize: 14,
          }}>İptal</button>
          <button onClick={handle} disabled={busy} style={{
            background: "rgba(229,115,115,0.12)", border: "1px solid rgba(229,115,115,0.4)",
            borderRadius: 8, color: "#e57373", padding: "9px 18px", cursor: "pointer",
            fontSize: 14, fontWeight: 700, opacity: busy ? 0.5 : 1,
          }}>
            {busy ? "Siliniyor..." : <span style={{display:"inline-flex",alignItems:"center",gap:6}}><IcTrash size={14} /> Sil</span>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Gider Formu ───────────────────────────────────────────────────────────────
const FIELDS = [
  { key: "electricity", label: "Elektrik", Icon: IcBolt    },
  { key: "water",       label: "Su",       Icon: IcDroplet },
  { key: "gas",         label: "Doğalgaz", Icon: IcFire    },
  { key: "rent",        label: "Kira",     Icon: IcHouse   },
  { key: "other",       label: "Diğer",    Icon: IcBox     },
];

function ExpenseForm({ year, month, onSaved, onDeleteClick }) {
  const [form, setForm]       = useState({ electricity: "", water: "", gas: "", rent: "", other: "", notes: "" });
  const [expenseId, setExpId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState(null);

  useEffect(() => {
    setLoading(true);
    apiFetch(`${API}/api/expenses/${year}/${month}`)
      .then(data => {
        const e = data.expense;
        setExpId(e.id);
        setForm({
          electricity: e.electricity || "",
          water:       e.water       || "",
          gas:         e.gas         || "",
          rent:        e.rent        || "",
          other:       e.other       || "",
          notes:       e.notes       || "",
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [year, month]);

  const handleChange = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    setSaving(true); setMsg(null);
    try {
      await apiFetch(`${API}/api/expenses`, {
        method: "POST",
        body: JSON.stringify({ year, month, ...form }),
      });
      setMsg({ ok: true, text: "✓ Kaydedildi" });
      onSaved?.();
    } catch (e) {
      setMsg({ ok: false, text: `✕ ${e.message}` });
    }
    setSaving(false);
    setTimeout(() => setMsg(null), 3000);
  };

  if (loading) return (
    <div style={{
      background: "var(--surface2)", borderRadius: 12, padding: "20px 22px",
      display: "flex", alignItems: "center", justifyContent: "center",
      color: "var(--text3)", fontSize: 13,
    }}>
      Yükleniyor...
    </div>
  );

  return (
    <div style={{ background: "var(--surface2)", borderRadius: 12, padding: "20px 22px", display: "flex", flexDirection: "column" }}>
      {/* Başlık */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 18 }}>
        <div>
          <div style={{ fontWeight: 700, color: "var(--text)", fontSize: 15 }}>Gider Girişi</div>
          <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 3 }}>
            {TR_MONTHS[month]} {year} dönemi
          </div>
        </div>
        {onDeleteClick && expenseId && (
          <button
            onClick={onDeleteClick}
            style={{
              background: "transparent", border: "none", color: "var(--text3)",
              fontSize: 11, cursor: "pointer", textDecoration: "underline",
              textDecorationStyle: "dotted", paddingTop: 2,
            }}
          >
            Veriyi sil
          </button>
        )}
      </div>

      {/* Alanlar */}
      <div style={{ display: "flex", flexDirection: "column", gap: 9, flex: 1 }}>
        {FIELDS.map(({ key, label, Icon }) => (
          <div key={key} style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <label style={{ width: 106, color: "var(--text2)", fontSize: 13, flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 8 }}>
              <Icon size={15} /> {label}
            </label>
            <div style={{ position: "relative", flex: 1 }}>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form[key]}
                onChange={e => handleChange(key, e.target.value)}
                placeholder="0.00"
                style={{
                  width: "100%", boxSizing: "border-box",
                  background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 7,
                  color: "var(--text)", padding: "9px 30px 9px 10px", fontSize: 13,
                  outline: "none",
                }}
              />
              <span style={{
                position: "absolute", right: 9, top: "50%", transform: "translateY(-50%)",
                color: "var(--text3)", fontSize: 11, pointerEvents: "none",
              }}>₺</span>
            </div>
          </div>
        ))}

        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: 4 }}>
          <label style={{ width: 106, color: "var(--text2)", fontSize: 13, flexShrink: 0, paddingTop: 8, display: "inline-flex", alignItems: "center", gap: 8 }}>
            <IcMemo size={15} /> Notlar
          </label>
          <textarea
            value={form.notes}
            onChange={e => handleChange("notes", e.target.value)}
            rows={2}
            placeholder="Opsiyonel açıklama..."
            style={{
              flex: 1, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 7,
              color: "var(--text)", padding: "9px 10px", fontSize: 13, resize: "vertical", outline: "none",
            }}
          />
        </div>
      </div>

      {/* Kaydet */}
      <div style={{ marginTop: 18, display: "flex", alignItems: "center", gap: 12 }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            flex: 1, background: "rgba(var(--mint-rgb),0.12)", border: "1px solid rgba(var(--mint-rgb),0.35)",
            borderRadius: 8, color: "var(--mint)", padding: "11px 0",
            cursor: saving ? "not-allowed" : "pointer",
            fontWeight: 700, fontSize: 14, opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? "Kaydediliyor..." : <span style={{display:"inline-flex",alignItems:"center",gap:8,justifyContent:"center"}}><IcSave size={16} /> Kaydet</span>}
        </button>
        {msg && (
          <span style={{ fontSize: 13, color: msg.ok ? "#4CAF50" : "#e57373", whiteSpace: "nowrap" }}>
            {msg.text}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Ana Bileşen ───────────────────────────────────────────────────────────────
export default function ExpensesTab() {
  const now = new Date();
  const [year,  setYear]  = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [detail,  setDetail]  = useState(null);
  const [summary, setSummary] = useState(null);
  const [loadingSum, setLoadingSum] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const loadDetail = useCallback(() => {
    apiFetch(`${API}/api/expenses/${year}/${month}`)
      .then(setDetail)
      .catch(() => setDetail(null));
  }, [year, month]);

  const loadSummary = useCallback(() => {
    setLoadingSum(true);
    apiFetch(`${API}/api/expenses/summary`)
      .then(data => setSummary(data.months))
      .catch(() => setSummary([]))
      .finally(() => setLoadingSum(false));
  }, []);

  useEffect(() => { loadDetail(); },  [loadDetail]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const goMonth = delta => {
    let m = month + delta, y = year;
    if (m > 12) { m = 1;  y++; }
    if (m < 1)  { m = 12; y--; }
    setMonth(m); setYear(y);
  };

  const handleExpenseDelete = async () => {
    const res = await fetch(`${API}/api/expenses/${year}/${month}`, {
      method: "DELETE", credentials: "include",
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `HTTP ${res.status}`);
    }
    setShowDeleteModal(false);
    loadDetail(); loadSummary();
  };

  const d = detail;
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;
  const netColor    = !d ? "var(--text3)" : d.true_net_profit >= 0 ? "#4CAF50" : "#e57373";
  const profitColor = !d ? "var(--text3)" : d.profit >= 0 ? "var(--text2)" : "#e57373";
  const totalGider  = d ? d.cost + d.operating_expenses : 0;

  return (
    <div className="tab-content" style={{ gap: 16 }}>

      {showDeleteModal && (
        <ConfirmPinModal
          title={`${TR_MONTHS[month]} ${year} Gider Verisini Sil`}
          warning={`${TR_MONTHS[month]} ${year} ayına ait tüm gider kaydı silinecek. Sipariş ve satış verileri etkilenmez. Bu işlem geri alınamaz.`}
          onConfirm={handleExpenseDelete}
          onClose={() => setShowDeleteModal(false)}
        />
      )}

      {/* ── Başlık + Ay Seçici ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <h2 style={{ margin: 0, color: "var(--text)", fontSize: 16, fontWeight: 700, display:"inline-flex", alignItems:"center", gap:8 }}>
          <IcMoney size={18} /> İşletme Finans
        </h2>

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <button onClick={() => goMonth(-1)} style={navBtnStyle}>◀</button>
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            background: "var(--surface2)", border: "1px solid var(--border)",
            borderRadius: 8, padding: "8px 20px", minWidth: 170, justifyContent: "center",
          }}>
            <span style={{ color: isCurrentMonth ? "#4CAF50" : "var(--text)", fontWeight: 700, fontSize: 15 }}>
              {TR_MONTHS[month]} {year}
            </span>
            {isCurrentMonth && (
              <span style={{
                fontSize: 9, color: "#4CAF50",
                background: "rgba(76,175,80,0.1)", borderRadius: 4,
                padding: "2px 5px", letterSpacing: "0.4px",
              }}>
                BU AY
              </span>
            )}
          </div>
          <button onClick={() => goMonth(1)} style={navBtnStyle}>▶</button>
          <button
            onClick={() => { setYear(now.getFullYear()); setMonth(now.getMonth() + 1); }}
            style={{ ...navBtnStyle, fontSize: 11, color: "var(--text3)", padding: "8px 12px" }}
          >
            Bugün
          </button>
        </div>
      </div>

      {/* ── Üst 2-Kolon: Özet + Form (mobilde tek sütun) ── */}
      <div className="exp-two-col">

        {/* Sol: Finans Özeti */}
        <div style={{ background: "var(--surface2)", borderRadius: 12, padding: "16px 18px", display: "flex", flexDirection: "column" }}>
          <div style={{ fontWeight: 700, color: "var(--text)", fontSize: 13, marginBottom: 12 }}>
            {TR_MONTHS[month]} {year} Özeti
          </div>

          {d ? (
            <>
              {/* 3 Ana Metrik */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 14 }}>
                {/* Satış Geliri */}
                <div style={{ background: "var(--surface)", borderRadius: 7, padding: "8px 6px", textAlign: "center" }}>
                  <div style={{ fontSize: 8, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: 5 }}>Satış Geliri</div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "var(--text)", letterSpacing: "-0.3px" }}>{fmt(d.revenue)}</div>
                </div>
                {/* Toplam Gider */}
                <div style={{ background: "var(--surface)", borderRadius: 7, padding: "8px 6px", textAlign: "center" }}>
                  <div style={{ fontSize: 8, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: 5 }}>Toplam Gider</div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#e8a030", letterSpacing: "-0.3px" }}>-{fmt(totalGider)}</div>
                </div>
                {/* Net Kâr */}
                <div style={{
                  background: "var(--surface)", borderRadius: 7, padding: "8px 6px", textAlign: "center",
                  border: `1px solid ${d.true_net_profit >= 0 ? "rgba(76,175,80,0.2)" : "rgba(229,115,115,0.2)"}`,
                }}>
                  <div style={{ fontSize: 8, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: 5 }}>Net Kâr</div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: netColor, letterSpacing: "-0.3px" }}>{fmt(d.true_net_profit)}</div>
                </div>
              </div>

              {/* Gider Detayı — flex:1 ile kalan yüksekliği doldurur */}
              <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <div style={{
                  fontSize: 8, color: "var(--text3)", textTransform: "uppercase",
                  letterSpacing: "0.8px", marginBottom: 6, paddingBottom: 5,
                  borderBottom: "1px solid var(--border)",
                }}>
                  Gider Detayı
                </div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                  {[
                    { label: "Ürün Maliyeti", Icon: null,      value: fmt(d.cost) },
                    { label: "Elektrik",      Icon: IcBolt,    value: fmt(d.expense.electricity) },
                    { label: "Su",            Icon: IcDroplet, value: fmt(d.expense.water) },
                    { label: "Doğalgaz",      Icon: IcFire,    value: fmt(d.expense.gas) },
                    { label: "Kira",          Icon: IcHouse,   value: fmt(d.expense.rent) },
                    { label: "Diğer",         Icon: IcBox,     value: fmt(d.expense.other) },
                  ].map(({ label, Icon, value }) => (
                    <div key={label} style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "5px 0", borderBottom: "1px solid var(--border)",
                    }}>
                      <span style={{ fontSize: 11, color: "var(--text3)", display: "inline-flex", alignItems: "center", gap: 6 }}>
                        {Icon && <Icon size={13} />} {label}
                      </span>
                      <span style={{ fontSize: 11, color: "var(--text2)" }}>{value}</span>
                    </div>
                  ))}
                </div>

                {/* Brüt Kâr */}
                <div style={{
                  display: "flex", justifyContent: "space-between",
                  padding: "8px 0 0", marginTop: 6, borderTop: "1px solid var(--border)",
                }}>
                  <span style={{ fontSize: 12, color: "var(--text2)", fontWeight: 600 }}>Brüt Kâr</span>
                  <span style={{ fontSize: 12, color: profitColor, fontWeight: 600 }}>{fmt(d.profit)}</span>
                </div>
              </div>
            </>
          ) : (
            <div style={{ color: "var(--text3)", fontSize: 13 }}>Yükleniyor...</div>
          )}
        </div>

        {/* Sağ: Gider Formu */}
        <ExpenseForm
          year={year}
          month={month}
          onSaved={() => { loadDetail(); loadSummary(); }}
          onDeleteClick={() => setShowDeleteModal(true)}
        />
      </div>

      {/* ── Z Raporu ── */}
      <ZReportSection year={year} month={month} />

      {/* ── Tam Genişlik Grafik ── */}
      <div style={{ background: "var(--surface2)", borderRadius: 12, padding: "20px 22px" }}>
        <div style={{ fontWeight: 700, color: "var(--text)", fontSize: 14, marginBottom: 14, display:"inline-flex", alignItems:"center", gap:8 }}>
          <IcTrend size={16} /> Son 12 Ay Finans Görünümü
        </div>
        {loadingSum ? (
          <div style={{ color: "var(--text3)", fontSize: 13, padding: "40px 0", textAlign: "center" }}>
            Yükleniyor...
          </div>
        ) : (
          <LineChart months={summary ?? []} />
        )}
      </div>

      {/* ── Son 12 Ay Tablosu ── */}
      <div style={{ background: "var(--surface2)", borderRadius: 12, padding: "20px 22px" }}>
        <div style={{ fontWeight: 700, color: "var(--text)", fontSize: 14, marginBottom: 14, display:"inline-flex", alignItems:"center", gap:8 }}>
          <IcClipboard size={16} /> Son 12 Ay Tablosu
        </div>
        {loadingSum ? (
          <div style={{ color: "var(--text3)", fontSize: 13 }}>Yükleniyor...</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  {["Ay", "Satış", "Ürün Maliyeti", "Brüt Kâr", "Faturalar", "Net Kâr"].map(h => (
                    <th key={h} style={{
                      color: "var(--text3)", textAlign: "right", padding: "8px 10px",
                      borderBottom: "1px solid var(--border)", fontWeight: 600,
                      fontSize: 10, textTransform: "uppercase", letterSpacing: "0.5px",
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(summary ?? []).slice().reverse().map((m, i) => {
                  const isCur = m.year === now.getFullYear() && m.month === (now.getMonth() + 1);
                  const nc    = m.true_net_profit >= 0 ? "#4CAF50" : "#e57373";
                  return (
                    <tr
                      key={m.period}
                      style={{
                        background: isCur
                          ? "rgba(76,175,80,0.06)"
                          : i % 2 === 0 ? "transparent" : "var(--surface3)",
                        borderLeft: isCur ? "2px solid rgba(76,175,80,0.4)" : "2px solid transparent",
                      }}
                    >
                      <td style={{
                        color: isCur ? "#4CAF50" : "var(--text2)",
                        padding: "9px 10px", textAlign: "right",
                        fontWeight: isCur ? 700 : 400,
                      }}>
                        {TR_MONTHS[m.month]} {m.year}
                      </td>
                      <td style={{ color: "var(--text2)", padding: "9px 10px", textAlign: "right" }}>{fmt(m.revenue)}</td>
                      <td style={{ color: "#e57373", padding: "9px 10px", textAlign: "right", opacity: 0.7 }}>{fmt(m.cost)}</td>
                      <td style={{ color: m.profit >= 0 ? "var(--text2)" : "#e57373", padding: "9px 10px", textAlign: "right" }}>{fmt(m.profit)}</td>
                      <td style={{ color: m.has_expenses ? "#e8a030" : "var(--border)", padding: "9px 10px", textAlign: "right" }}>
                        {m.has_expenses ? fmt(m.operating_expenses) : "—"}
                      </td>
                      <td style={{
                        color: m.has_expenses ? nc : "var(--border)",
                        padding: "9px 10px", textAlign: "right",
                        fontWeight: m.has_expenses ? 700 : 400,
                      }}>
                        {m.has_expenses ? fmt(m.true_net_profit) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ marginTop: 8, fontSize: 11, color: "var(--text3)" }}>
          "—" = O ay için gider kaydı girilmemiş. Net Kâr = Brüt Kâr − Faturalar.
        </div>
      </div>

    </div>
  );
}

const navBtnStyle = {
  background: "var(--surface2)",
  border: "1px solid var(--border)",
  borderRadius: 7,
  color: "var(--text2)",
  padding: "8px 14px",
  minHeight: 38,
  cursor: "pointer",
  fontSize: 14,
};
